import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { getBlogPost, getProducts, getBlogPosts } from '@/lib/api';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS } from '@/data/site';
import { LOCATIONS } from '@/data/locations';
import ProductCard from '@/components/ProductCard';

const TOPIC_MAP = [
  { kw: ['indica'], category: 'indica', label: 'Shop Indica Flower' },
  { kw: ['sativa'], category: 'sativa', label: 'Shop Sativa Flower' },
  { kw: ['hybrid'], category: 'hybrid', label: 'Shop Hybrid Flower' },
  { kw: ['edible', 'dose', 'dosing', 'gumm'], category: 'edibles', label: 'Shop Edibles' },
  { kw: ['grind', 'roll', 'joint', 'pre-roll', 'preroll'], category: 'prerolls', label: 'Shop Pre-Rolls' },
  { kw: ['vape', 'cartridge', 'cart'], category: 'cartridges', label: 'Shop Vapes & Carts' },
  { kw: ['concentrate', 'dab', 'wax', 'diamond', 'shatter'], category: 'concentrates', label: 'Shop Concentrates' },
  { kw: ['terpene', 'strain', 'flower', 'decarboxyl', 'cannabinoid', 'thc', 'cbd', 'cbn'], category: 'exotic', label: 'Shop Exotic Flower' },
];

function topicFor(post) {
  const text = `${post.slug} ${post.title}`.toLowerCase();
  for (const t of TOPIC_MAP) {
    if (t.kw.some((k) => text.includes(k))) return t;
  }
  return { category: 'exotic', label: 'Shop Exotic Flower' };
}

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading } = useQuery({ queryKey: ['blog-post', slug], queryFn: () => getBlogPost(slug), staleTime: 120000 });
  const { data: allPosts } = useQuery({ queryKey: ['blog-posts'], queryFn: getBlogPosts, staleTime: 120000 });
  const topic = post ? topicFor(post) : null;
  const { data: related } = useQuery({
    queryKey: ['blog-related', topic?.category],
    queryFn: () => getProducts({ category: topic.category, per_page: 4 }),
    enabled: !!topic,
    staleTime: 120000,
  });

  const morePosts = (allPosts || []).filter((p) => p.slug !== slug).slice(0, 3);
  const nearbyLocations = LOCATIONS.slice(0, 6);

  if (isLoading) {
    return <div className="mx-auto max-w-3xl px-6 pb-28 pt-32"><div className="h-96 animate-pulse bg-parchment" /></div>;
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-3xl px-6 pb-28 pt-32 text-center">
        <h1 className="text-3xl font-black text-ink">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-block bg-forest px-8 py-3 text-xs font-bold uppercase tracking-[0.2em] text-bone">Back to Blog</Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${post.title} | Majestic Greens DMV Blog`}
        description={post.excerpt || `${post.title} — cannabis guide from Majestic Greens DMV, Washington DC.`}
        path={`/blog/${post.slug}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            datePublished: post.date,
            image: post.image || undefined,
            author: { '@type': 'Organization', name: BUSINESS.name },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://majesticgreensdmv.com/' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://majesticgreensdmv.com/blog' },
              { '@type': 'ListItem', position: 3, name: post.title, item: `https://majesticgreensdmv.com/blog/${post.slug}` },
            ],
          },
        ]}
      />
      <article data-testid={`blog-post-${post.slug}`} className="pb-28">
        <div className="border-b border-ink/10 bg-parchment py-16">
          <div className="mx-auto max-w-3xl px-6">
            <Reveal>
              <Link to="/blog" data-testid="back-to-blog" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage transition-colors hover:text-forest">
                <ArrowLeft className="h-4 w-4" /> All guides
              </Link>
              <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">{post.date} · {BUSINESS.name}</p>
              <h1 className="mt-4 text-3xl font-black leading-tight tracking-[-0.02em] text-ink sm:text-5xl">{post.title}</h1>
            </Reveal>
          </div>
        </div>

        {post.image && (
          <div className="mx-auto max-w-4xl px-6 pt-10">
            <img src={post.image} alt={post.title} className="w-full border border-ink/10 object-cover" />
          </div>
        )}

        <div className="mx-auto max-w-3xl px-6 pt-10">
          <div className="blog-content" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          <Reveal>
            <div className="mt-14 border border-ink/10 bg-forest p-8">
              <h2 className="text-xl font-extrabold tracking-tight text-bone">Put it into practice</h2>
              <p className="mt-2 text-sm text-bone/70">
                Everything in this guide is on the live menu at {BUSINESS.name} — ${BUSINESS.minOrder} minimum, pickup at {BUSINESS.address} or delivery across DC.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={`/shop/${topic.category}`} data-testid="post-topic-cta" className="inline-flex items-center gap-2 bg-gold px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-[#c9a227]">
                  {topic.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/shop" data-testid="post-menu-cta" className="inline-flex items-center gap-2 border border-bone/30 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-bone transition-colors hover:border-bone">
                  Full Menu
                </Link>
              </div>
            </div>
          </Reveal>

          {related && related.length > 0 && (
            <Reveal>
              <h2 className="mt-16 text-2xl font-extrabold tracking-tight text-ink">Related products</h2>
              <div className="mt-6 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2">
                {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            </Reveal>
          )}

          <Reveal>
            <h2 className="mt-16 text-2xl font-extrabold tracking-tight text-ink">Where we serve</h2>
            <div className="mt-5 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3">
              {nearbyLocations.map((loc) => (
                <Link key={loc.slug} to={`/${loc.slug}`} data-testid={`post-location-${loc.slug}`} className="group flex items-center justify-between bg-bone px-4 py-3 transition-colors hover:bg-parchment">
                  <span className="text-sm font-semibold text-sage group-hover:text-ink">{loc.name}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-ash group-hover:text-forest" />
                </Link>
              ))}
            </div>
            <Link to="/service-area" data-testid="post-areas-link" className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-forest hover:text-moss">
              All service areas <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          {morePosts.length > 0 && (
            <Reveal>
              <h2 className="mt-16 text-2xl font-extrabold tracking-tight text-ink">Keep reading</h2>
              <div className="mt-5 divide-y divide-ink/5 border border-ink/10">
                {morePosts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`more-post-${p.slug}`} className="group flex items-center justify-between bg-bone px-6 py-5 transition-colors hover:bg-parchment">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">{p.date}</span>
                      <p className="mt-1 font-extrabold tracking-tight text-ink group-hover:text-forest">{p.title}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-ash transition-all group-hover:translate-x-1 group-hover:text-forest" />
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </article>
    </>
  );
}
